import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ChatMessage, notificationsUser } from "@/entities/messaging/types";
import { findCachedReport } from "@/entities/savedReports/hooks";
import { getCurrentTimeUTC } from "@/utils/datetime";
import { reportTargetWithoutOrigin } from "@/entities/savedReports/types";
import {
  FinishedReportRun,
  useReportRuns
} from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { ReportRunToast } from "@/components/global/Chat/components/dreamerReports/ReportRunToast";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

const NOTICE_ID_PREFIX = "report-run-";

interface UseReportRunNotificationsOptions {
  chatDrawerOpen: boolean;
  setChatDrawerOpen: (isOpen: boolean) => void;
  /** Shows a notice on the Dreamer button, and as a browser notification. */
  notify: (messages: ChatMessage[]) => void;
}

/**
 * Tells the user a report run finished while its modal was closed: on the
 * Dreamer button and as a browser notification when the chat is closed, as a
 * toast when it is open. The notice is never a chat message. Opening it opens
 * the chat on that report.
 */
export const useReportRunNotifications = ({
  chatDrawerOpen,
  setChatDrawerOpen,
  notify
}: UseReportRunNotificationsOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setOnRunFinished, focusReport } = useReportRuns();

  const openReport = useCallback(
    (reportId: string, reportName: string | null) => {
      // The run's event names the report but not the chart it was saved from,
      // which the preview needs; the reports list has it when it was loaded.
      // Without it the preview still shows the run's own result.
      const target =
        findCachedReport(queryClient, reportId) ??
        reportTargetWithoutOrigin({
          id: reportId,
          name: reportName ?? undefined
        });
      setChatDrawerOpen(true);
      focusReport(target);
    },
    [queryClient, setChatDrawerOpen, focusReport]
  );

  /** Opens the report a notice built here names; any other message is ignored. */
  const openReportRunNotice = useCallback(
    (message: ChatMessage) => {
      if (!message.reportRunId || !message.savedReportId) return;
      openReport(message.savedReportId, message.reportName ?? null);
    },
    [openReport]
  );

  // The handler is registered once; these change every render. Written after
  // commit, not during render, so a render React discards cannot leave them
  // pointing at values that never committed.
  const latestRef = useRef({ chatDrawerOpen, notify, openReport });
  useEffect(() => {
    latestRef.current = { chatDrawerOpen, notify, openReport };
  });

  useEffect(() => {
    const onRunFinished = (run: FinishedReportRun) => {
      const { chatDrawerOpen: isDrawerOpen, notify: notifyNow, openReport: open } =
        latestRef.current;
      const isReady = run.status === "success";
      const text = run.reportName
        ? t(`${TRANSLATION_KEY}.runNotice.${isReady ? "ready" : "failed"}`, {
            name: run.reportName
          })
        : t(
            `${TRANSLATION_KEY}.runNotice.${isReady ? "readyUnnamed" : "failedUnnamed"}`
          );
      const noticeId = `${NOTICE_ID_PREFIX}${run.runId}`;

      if (isDrawerOpen) {
        const show = isReady ? toast.success : toast.error;
        show(
          (shown) => (
            <ReportRunToast
              message={text}
              onOpenReport={() => open(run.reportId, run.reportName)}
              onDismiss={() => toast.dismiss(shown.id)}
            />
          ),
          { id: noticeId }
        );
        return;
      }

      notifyNow([
        {
          id: noticeId,
          user: notificationsUser,
          message: text,
          timestamp: getCurrentTimeUTC().toString(),
          savedReportId: run.reportId,
          reportName: run.reportName,
          reportRunId: run.runId
        }
      ]);
    };

    setOnRunFinished(onRunFinished);
    return () => setOnRunFinished(null);
  }, [setOnRunFinished, t]);

  return { openReportRunNotice };
};
